import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Ban, CheckCircle, Eye, ShieldAlert, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardHeader, CardTitle, MetricCard, PageHeader, ProductIntro, DonutChart, SectionEmptyState } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

interface RiskSummary {
  totalEvents: number;
  allowedCount: number;
  challengeCount: number;
  reviewCount: number;
  blockedCount: number;
  lowRiskCount: number;
  mediumRiskCount: number;
  highRiskCount: number;
  criticalRiskCount: number;
  averageTrustScore: number;
  averageRiskScore: number;
}

interface RiskEvent {
  transactionId: string;
  productCode: string;
  mobileNumberMasked: string;
  operator: string;
  countryCode: string;
  riskScore: number;
  riskLevel: string;
  decision: string;
  reasonCodes: string[];
  signals?: Record<string, unknown>;
  createdAt: string;
}

interface PageDto<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

async function fetchRiskSummary(): Promise<RiskSummary> {
  const res = await api.get<ResponseEnvelope<RiskSummary>>(Endpoints.me.riskSummary, {
    params: { productCode: 'SHIELD' },
  });
  return unwrap(res);
}
async function fetchHighRiskEvents(): Promise<PageDto<RiskEvent>> {
  const res = await api.get<ResponseEnvelope<PageDto<RiskEvent>>>(Endpoints.me.riskEvents, {
    params: { productCode: 'SHIELD', riskLevel: 'HIGH', page: 0, size: 8 },
  });
  return unwrap(res);
}
async function fetchBlockedEvents(): Promise<PageDto<RiskEvent>> {
  const res = await api.get<ResponseEnvelope<PageDto<RiskEvent>>>(Endpoints.me.riskEvents, {
    params: { productCode: 'SHIELD', decision: 'BLOCK', page: 0, size: 8 },
  });
  return unwrap(res);
}

function riskBadgeClass(level: string) {
  switch (level) {
    case 'LOW':      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'MEDIUM':   return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    case 'HIGH':     return 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300';
    case 'CRITICAL': return 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300';
    default:         return 'bg-surface-muted text-fg-muted';
  }
}

function decisionInfo(d: string): { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' } {
  switch (d) {
    case 'ALLOW':     return { label: 'Allow',     tone: 'success'  };
    case 'CHALLENGE': return { label: 'Challenge', tone: 'warning'  };
    case 'REVIEW':    return { label: 'Review',    tone: 'neutral'  };
    case 'BLOCK':     return { label: 'Block',     tone: 'danger'   };
    default:          return { label: d,           tone: 'neutral'  };
  }
}

const REASON_LABELS: Record<string, { label: string; tone: 'danger' | 'warning' | 'success' | 'neutral' }> = {
  DEVICE_ROOTED:             { label: 'Rooted device',        tone: 'danger'  },
  EMULATOR_DETECTED:         { label: 'Emulator',             tone: 'danger'  },
  VPN_DETECTED:              { label: 'VPN',                  tone: 'warning' },
  APP_INTEGRITY_FAIL:        { label: 'Integrity fail',        tone: 'danger'  },
  STUCK_PLUS_UNRESOLVED:     { label: 'Stuck+ unresolved',    tone: 'danger'  },
  REPEATED_ATTEMPT_PATTERN:  { label: 'Repeated attempts',    tone: 'warning' },
  RECENT_FAILURE_BURST:      { label: 'Failure burst',        tone: 'warning' },
  OPERATOR_CHANGE:           { label: 'Operator change',      tone: 'warning' },
  DEVICE_INCONSISTENT:       { label: 'Device inconsistent',  tone: 'warning' },
  UNKNOWN_OPERATOR:          { label: 'Unknown operator',     tone: 'neutral' },
  NO_DEVICE_SIGNAL:          { label: 'No device signal',     tone: 'neutral' },
  SDK_SESSION_VERIFIED:      { label: 'SDK verified',         tone: 'success' },
  DEVICE_TRUSTED:            { label: 'Device trusted',       tone: 'success' },
  NETWORK_VERIFIED:          { label: 'Cellular network',     tone: 'success' },
};

function fmt(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function ClientFraudProtectionPage() {
  const summaryQ  = useQuery({ queryKey: ['me','risk-summary','SHIELD'], queryFn: fetchRiskSummary,    staleTime: 60_000 });
  const highRiskQ = useQuery({ queryKey: ['me','shield-events','high'],   queryFn: fetchHighRiskEvents, staleTime: 60_000 });
  const blockedQ  = useQuery({ queryKey: ['me','shield-events','block'],  queryFn: fetchBlockedEvents,  staleTime: 60_000 });

  const s = summaryQ.data;

  const decisionSegments = s ? [
    { label: 'Allow',     value: s.allowedCount,   color: '#10b981' },
    { label: 'Challenge', value: s.challengeCount, color: '#f59e0b' },
    { label: 'Review',    value: s.reviewCount,    color: '#3b82f6' },
    { label: 'Block',     value: s.blockedCount,   color: '#ef4444' },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Fraud Protection"
        subtitle="Shield point-in-time risk assessments for your integration."
        badge={<Badge tone="shield">Shield</Badge>}
      />

      <ProductIntro
        icon={Shield}
        tone="shield"
        eyebrow="Customer Intelligence · Shield"
        title="Stop account takeover at the moment of intent — without taxing your trusted customers."
        points={['Real-time decision', 'Allow / Challenge / Review / Block', 'Explainable reason codes', 'No rip-and-replace']}
      >
        <p>
          <strong>Authmatech Shield</strong> evaluates a customer’s live session the instant they attempt a
          sensitive action — login, payout, password reset, a high-value transaction — and returns a ready-made
          decision: <strong>Allow, Challenge, Review,</strong> or <strong>Block</strong>.
        </p>
        <p>
          It weighs the device’s integrity, the network it’s on, how the number has behaved over the last hours,
          and whether the customer is stuck mid-journey. Trusted customers sail through; risky ones get exactly
          the right amount of friction — and every decision ships with the reason codes behind it, so it drops
          into the IAM, fraud and risk tools you already run.
        </p>
      </ProductIntro>

      {/* Shield hero strip */}
      <div
        className="relative overflow-hidden rounded-2xl px-6 py-5"
        style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 60%, #047857 100%)' }}
      >
        <div className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 rounded-full bg-white/[0.04]" aria-hidden />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/60">Total Risk Checks</p>
              <p className="text-4xl font-extrabold tracking-tight text-white">
                {s ? s.totalEvents.toLocaleString() : '—'}
              </p>
            </div>
          </div>
          {s && (
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{s.blockedCount.toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Blocked</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{(s.reviewCount + s.challengeCount).toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Under review</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{s.allowedCount.toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Allowed</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Risk Checks" value={s?.totalEvents ?? 0} icon={ShieldAlert} tone="shield" loading={summaryQ.isLoading} />
        <MetricCard label="Blocked" value={s?.blockedCount ?? 0} subtitle={s && s.totalEvents ? `${((s.blockedCount / s.totalEvents) * 100).toFixed(1)}% of total` : undefined} icon={Ban} tone="danger" loading={summaryQ.isLoading} />
        <MetricCard label="Under Review" value={s ? (s.reviewCount + s.challengeCount) : 0} subtitle="Review + Challenge" icon={Eye} tone="warning" loading={summaryQ.isLoading} />
        <MetricCard label="Allowed" value={s?.allowedCount ?? 0} subtitle={s && s.totalEvents ? `${((s.allowedCount / s.totalEvents) * 100).toFixed(1)}% of total` : undefined} icon={CheckCircle} tone="success" loading={summaryQ.isLoading} />
      </div>

      {/* Breakdown + Donut */}
      {s && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Risk Level Breakdown</CardTitle></CardHeader>
            <div className="space-y-3">
              {[
                { label: 'Low',      count: s.lowRiskCount,      color: '#10b981', bg: 'from-emerald-500 to-emerald-400' },
                { label: 'Medium',   count: s.mediumRiskCount,   color: '#f59e0b', bg: 'from-amber-500 to-amber-400'     },
                { label: 'High',     count: s.highRiskCount,     color: '#f97316', bg: 'from-orange-500 to-orange-400'   },
                { label: 'Critical', count: s.criticalRiskCount, color: '#ef4444', bg: 'from-rose-500 to-rose-400'       },
              ].map(({ label, count, color, bg }) => {
                const pct = s.totalEvents ? (count / s.totalEvents) * 100 : 0;
                return (
                  <div key={label}>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: color }} />
                        <span className="font-medium text-fg">{label}</span>
                      </div>
                      <div className="flex items-center gap-2 text-fg-muted">
                        <span className="font-semibold text-fg tabular-nums">{count.toLocaleString()}</span>
                        <span className="text-fg-subtle">({pct.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
                      <div className={`h-full rounded-full bg-gradient-to-r ${bg} transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="flex flex-col items-center">
            <CardHeader className="w-full"><CardTitle>Decision Breakdown</CardTitle></CardHeader>
            {decisionSegments.some((d) => d.value > 0) ? (
              <DonutChart segments={decisionSegments} centerValue={s.totalEvents} centerLabel="total" size={160} thickness={22} />
            ) : (
              <SectionEmptyState title="No decisions yet" className="py-8" />
            )}
          </Card>
        </div>
      )}

      {/* High-risk events */}
      <Card noPadding>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <CardTitle>Recent High-Risk Events</CardTitle>
          <Link to="/portal/risk-events?productCode=SHIELD&riskLevel=HIGH" className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
            View all →
          </Link>
        </div>
        {highRiskQ.isLoading ? (
          <div className="divide-y divide-border">
            {[0,1,2,3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 px-5 py-3">
                <div className="h-3 w-32 rounded bg-surface-muted" />
                <div className="h-3 w-24 rounded bg-surface-muted ml-auto" />
              </div>
            ))}
          </div>
        ) : !highRiskQ.data?.content.length ? (
          <SectionEmptyState icon={CheckCircle} title="No high-risk events" description="Your integration looks healthy." className="py-10" />
        ) : (
          <ShieldEventTable events={highRiskQ.data.content} />
        )}
      </Card>

      {/* Blocked events */}
      <Card noPadding>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <CardTitle>Recently Blocked</CardTitle>
          <Link to="/portal/risk-events?productCode=SHIELD&decision=BLOCK" className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
            View all →
          </Link>
        </div>
        {blockedQ.isLoading ? (
          <div className="divide-y divide-border">
            {[0,1,2,3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 px-5 py-3">
                <div className="h-3 w-32 rounded bg-surface-muted" />
                <div className="h-3 w-24 rounded bg-surface-muted ml-auto" />
              </div>
            ))}
          </div>
        ) : !blockedQ.data?.content.length ? (
          <SectionEmptyState icon={CheckCircle} title="No blocked requests" description="No requests have been blocked recently." className="py-10" />
        ) : (
          <ShieldEventTable events={blockedQ.data.content} />
        )}
      </Card>

      <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-500/20 dark:bg-amber-500/10">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <p className="text-sm text-amber-800 dark:text-amber-300">
          Blocked and review decisions still deduct a credit from your balance. See the{' '}
          <Link to="/portal/risk-events?productCode=SHIELD" className="font-semibold underline underline-offset-2">full Shield event log</Link>{' '}
          for detailed reason codes, consistency signals, and device data.
        </p>
      </div>
    </div>
  );
}

function ShieldEventTable({ events }: { events: RiskEvent[] }) {
  return (
    <div className="divide-y divide-border">
      {events.map((e) => {
        const { label, tone } = decisionInfo(e.decision);
        const sdkVerified = e.signals?.['sdkVerified'] === true;
        const deviceLevel  = e.signals?.['deviceConsistencyLevel'] as string | undefined;
        const operatorLevel = e.signals?.['operatorConsistencyLevel'] as string | undefined;
        return (
          <div key={e.transactionId} className="px-5 py-3">
            {/* Main row */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono text-fg truncate">{e.transactionId}</p>
                <p className="text-[11px] text-fg-muted">{fmt(e.createdAt)}{e.mobileNumberMasked ? ` · ${e.mobileNumberMasked}` : ''}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={cn('inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold', riskBadgeClass(e.riskLevel))}>
                  {e.riskLevel}
                </span>
                <span className="text-xs font-bold tabular-nums text-fg">{e.riskScore}</span>
                <Badge tone={tone}>{label}</Badge>
              </div>
            </div>
            {/* Reason badges */}
            {(e.reasonCodes?.length > 0 || sdkVerified || deviceLevel || operatorLevel) && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {e.reasonCodes?.map((code) => {
                  const info = REASON_LABELS[code];
                  if (!info) return null;
                  return (
                    <span
                      key={code}
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-medium',
                        info.tone === 'danger'  && 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
                        info.tone === 'warning' && 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
                        info.tone === 'success' && 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
                        info.tone === 'neutral' && 'bg-surface-subtle text-fg-muted',
                      )}
                    >
                      {info.label}
                    </span>
                  );
                })}
                {deviceLevel && deviceLevel !== 'UNKNOWN' && (
                  <span className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-medium',
                    deviceLevel === 'STABLE'       ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' :
                    deviceLevel === 'INCONSISTENT' ? 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300' :
                    'bg-surface-subtle text-fg-muted'
                  )}>
                    Device: {deviceLevel.toLowerCase()}
                  </span>
                )}
                {operatorLevel && operatorLevel !== 'UNKNOWN' && (
                  <span className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-medium',
                    operatorLevel === 'STABLE'   ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' :
                    operatorLevel === 'CHANGED'  ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' :
                    'bg-surface-subtle text-fg-muted'
                  )}>
                    Operator: {operatorLevel.toLowerCase()}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
